import { TopLevelDomain } from '../../top-level-domains/types';
import * as TopLevelDomainsService from '../../top-level-domains/services/TopLevelDomainsService';

/**
 * TLD Service for validation and auto-creation of Top-Level Domains
 * Handles TLD validation, extraction from domain names, and automatic creation
 */
export class TLDService {
    // Standard TLD validation regex - allows letters only, 2-63 characters
    private static readonly VALID_TLD_REGEX = /^[a-zA-Z]{2,63}$/;
    
    // Common valid TLDs for additional validation (can be expanded)
    private static readonly COMMON_TLDS = new Set([
        'com', 'org', 'net', 'edu', 'gov', 'mil', 'int', 'co', 'io', 'ai',
        'uk', 'us', 'ca', 'au', 'de', 'fr', 'jp', 'cn', 'in', 'br',
        'info', 'biz', 'name', 'pro', 'museum', 'travel', 'jobs', 'mobi',
        'tel', 'asia', 'cat', 'coop', 'aero', 'post', 'xxx', 'app',
        'dev', 'tech', 'online', 'site', 'website', 'store', 'blog'
    ]);

    /**
     * Validates if a TLD format is valid according to standard rules
     * @param tld - The TLD string to validate
     * @returns boolean indicating if the TLD format is valid
     */
    static isValidTLD(tld: string): boolean {
        if (!tld || typeof tld !== 'string') {
            return false;
        }

        const cleanTld = tld.toLowerCase().trim();
        
        // Check basic format requirements
        if (!this.VALID_TLD_REGEX.test(cleanTld)) {
            return false;
        }

        // Additional length validation
        if (cleanTld.length < 2 || cleanTld.length > 63) {
            return false;
        }

        return true;
    }

    /**
     * Extracts TLD from a domain name
     * @param domainName - Full domain name (e.g., "example.com")
     * @returns The TLD portion or null if invalid
     */
    static extractTLDFromDomain(domainName: string): string | null {
        if (!domainName || typeof domainName !== 'string') {
            return null;
        }

        const cleanDomain = domainName.toLowerCase().trim();
        const parts = cleanDomain.split('.');
        
        if (parts.length < 2) {
            return null;
        }

        const tld = parts[parts.length - 1];
        return this.isValidTLD(tld) ? tld : null;
    }

    /**
     * Validates and creates a TLD if it doesn't exist in the system
     * @param tldName - The TLD name to validate and create
     * @returns Promise<TopLevelDomain> - The existing or newly created TLD
     * @throws Error if TLD is invalid or creation fails
     */
    static async validateAndCreateTLD(tldName: string): Promise<TopLevelDomain> {
        if (!tldName) {
            throw new Error('TLD name is required');
        }

        const cleanTldName = tldName.toLowerCase().trim();

        // Validate TLD format
        if (!this.isValidTLD(cleanTldName)) {
            throw new Error(`Invalid TLD format: ${tldName}. TLD must be 2-63 characters long and contain only letters.`);
        }

        try {
            // Check if TLD already exists
            const existingTLDs = await TopLevelDomainsService.fetchRecords({
                name: { eq: cleanTldName }
            });

            if (existingTLDs && existingTLDs.length > 0) {
                return existingTLDs[0];
            }

            // Create new TLD if it doesn't exist
            return await this.createTLD(cleanTldName);

        } catch (error) {
            console.error(`Error validating and creating TLD ${tldName}:`, error);
            throw new Error(`Failed to validate and create TLD ${tldName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Extracts TLD from domain name and ensures it exists in the system
     * @param domainName - Full domain name
     * @returns Promise<TopLevelDomain> - The TLD record
     * @throws Error if domain is invalid or TLD creation fails
     */
    static async extractAndEnsureTLD(domainName: string): Promise<TopLevelDomain> {
        if (!domainName) {
            throw new Error('Domain name is required');
        }

        const tldName = this.extractTLDFromDomain(domainName);
        if (!tldName) {
            throw new Error(`Could not extract valid TLD from domain: ${domainName}`);
        }

        return await this.validateAndCreateTLD(tldName);
    }

    /**
     * Creates a new TLD record in the system
     * @param tldName - The TLD name to create
     * @returns Promise<TopLevelDomain> - The created TLD record
     * @throws Error if creation fails
     */
    private static async createTLD(tldName: string): Promise<TopLevelDomain> {
        try {
            const tldInput = {
                name: tldName,
                tldType: this.determineTLDType(tldName),
                tldManager: 'Auto-created' // Indicates this was automatically created
            };

            const createdTLD = await TopLevelDomainsService.add(tldInput);
            if (!createdTLD) {
                throw new Error(`Failed to create TLD record for: ${tldName}`);
            }

            console.log(`Successfully created TLD: ${tldName}`);
            return createdTLD;

        } catch (error) {
            console.error(`Error creating TLD ${tldName}:`, error);
            throw error; // Re-throw the original error to preserve the message format
        }
    }

    /**
     * Determines the type of TLD based on common patterns
     * @param tldName - The TLD name
     * @returns string - The determined TLD type
     */
    private static determineTLDType(tldName: string): string {
        const cleanTld = tldName.toLowerCase();

        // Country code TLDs (2 characters)
        if (cleanTld.length === 2) {
            return 'country-code';
        }

        // Common generic TLDs
        if (this.COMMON_TLDS.has(cleanTld)) {
            return 'generic';
        }

        // Default to generic for unknown TLDs
        return 'generic';
    }

    /**
     * Batch validates multiple TLD names
     * @param tldNames - Array of TLD names to validate
     * @returns Object with valid and invalid TLD arrays
     */
    static validateTLDBatch(tldNames: string[]): { valid: string[], invalid: string[] } {
        const valid: string[] = [];
        const invalid: string[] = [];

        for (const tld of tldNames) {
            if (this.isValidTLD(tld)) {
                valid.push(tld.toLowerCase().trim());
            } else {
                invalid.push(tld);
            }
        }

        return { valid, invalid };
    }

    /**
     * Batch creates multiple TLDs that don't exist
     * @param tldNames - Array of TLD names to create
     * @returns Promise with results of creation attempts
     */
    static async createMissingTLDs(tldNames: string[]): Promise<{
        created: TopLevelDomain[],
        existing: TopLevelDomain[],
        failed: { tld: string, error: string }[]
    }> {
        const created: TopLevelDomain[] = [];
        const existing: TopLevelDomain[] = [];
        const failed: { tld: string, error: string }[] = [];

        for (const tldName of tldNames) {
            try {
                const result = await this.validateAndCreateTLD(tldName);
                
                // Check if this was newly created or existing
                const existingTLDs = await TopLevelDomainsService.fetchRecords({
                    name: { eq: tldName.toLowerCase().trim() }
                });
                
                if (existingTLDs && existingTLDs.length > 1) {
                    existing.push(result);
                } else {
                    created.push(result);
                }
                
            } catch (error) {
                failed.push({
                    tld: tldName,
                    error: error instanceof Error ? error.message : 'Unknown error'
                });
            }
        }

        return { created, existing, failed };
    }
}

export default TLDService;